import { codePointLength } from '@app/lib/expressions/ast';
import { formatExpressionValue } from '@app/lib/expressions/evaluator';
import { FieldTypes, StandardFormFieldDto } from '@app/models/dtos/form';
import { dateRangeDays } from '@app/utils/date-range';
import { isIsoDate } from '@app/utils/iso-date';
import { answerTags, tagKey } from '@app/utils/tags';
import { CONTAINER_LOGIC, containerLogicIndex, formHasContainers } from '@app/utils/layout-containers';
import { isTime } from '@app/utils/time-of-day';
import { pruneOptionLogic } from '@app/utils/option-logic-checks';
import { sourcedAnswerTexts } from '@app/utils/option-sources';
import { Comparison, FieldConditionalLogic, JUMP_TARGET_SUBMIT, LogicalOperator, LogicCondition, PageJump } from '@app/models/types/form-builder-shared';

export type { FieldConditionalLogic, LogicAction, LogicCondition, PageJump } from '@app/models/types/form-builder-shared';

/**
 * Conditional logic (v2 builder) — show/hide a question based on earlier answers.
 *
 * This is deliberately separate from the older `vvalidation-utils` engine, which
 * was written for the v1 builder: it keys comparisons off `FormBuilderTagNames`
 * and toggles whole slides, neither of which matches the v2 slide/field model.
 *
 * The rule lives on the *target* field at `field.properties.logic`:
 *   - `action: 'SHOW'` → the field is hidden until the conditions are met.
 *   - `action: 'HIDE'` → the field is visible until the conditions are met.
 * Conditions reference an earlier field's answer by id + type, so we can read the
 * right slot out of the stored answer.
 *
 * Choice questions store the chosen option's id, while a condition names the
 * option by its text (what the builder and the AI operations store) or by its
 * id. The evaluators therefore take the form's questions by id (`LogicSources`,
 * from `logicSourcesById`) and match a condition against the chosen option's
 * id, text or label. The server's twin is backend services/repeating_groups.py.
 */

/** The form's questions by id (page questions and the questions of repeating groups). */
export type LogicSources = Record<string, StandardFormFieldDto>;

export { CONTAINER_LOGIC };

/** Every question of a form by id, as the evaluators read a choice condition's options from it. */
export function logicSourcesById(slides: Array<StandardFormFieldDto> | undefined | null): LogicSources {
    // No prototype: a question id is creator content and never names an inherited key.
    const byId: LogicSources = Object.create(null);
    (slides ?? []).forEach((slide) =>
        slide?.properties?.fields?.forEach((field) => {
            if (field?.id) byId[field.id] = field;
            if (field?.type === FieldTypes.GROUP) field.properties?.fields?.forEach((child) => child?.id && (byId[child.id] = child));
        })
    );
    // the logic of the layout containers (utils/layout-containers.ts)
    if (formHasContainers(slides)) (byId as any)[CONTAINER_LOGIC] = containerLogicIndex(slides);
    return byId;
}

/** Pull the comparable value out of a stored v2 answer for a given source field type. */
export function getComparableAnswerValue(answer: any, fieldType: FieldTypes | string): any {
    if (!answer) return undefined;
    switch (fieldType) {
        case FieldTypes.YES_NO:
            return typeof answer.boolean === 'boolean' ? (answer.boolean ? 'Yes' : 'No') : undefined;
        case FieldTypes.SHORT_TEXT:
        case FieldTypes.LONG_TEXT:
            return answer.text;
        case FieldTypes.EMAIL:
            return answer.email ?? answer.text;
        case FieldTypes.LINK:
            return answer.url ?? answer.text;
        case FieldTypes.NUMBER:
        case FieldTypes.RATING:
        case FieldTypes.LINEAR_RATING:
            return answer.number;
        case FieldTypes.DATE:
        case FieldTypes.DAY:
            return answer.date;
        case FieldTypes.TIME:
            return answer.time;
        case FieldTypes.CURRENCY:
            // the amount as written ("12.50"): a number to formulas and ordered comparisons; the code is not part of the value
            return typeof answer.currency?.amount === 'string' && answer.currency.amount ? answer.currency.amount : undefined;
        case FieldTypes.DATE_RANGE: {
            // {start, end} once either is given (conditions and formulas read the two days: `dateRangeDays`)
            const { start, end } = dateRangeDays(answer);
            return start || end ? { start, end } : undefined;
        }
        case FieldTypes.TAGS:
            return answerTags(answer);
        case FieldTypes.ADDRESS: {
            // the postcode as written (conditions compare it as text: "starts with 10"); the other parts are not conditions
            const postcode = answer.address?.postcode;
            return typeof postcode === 'string' && postcode ? postcode : undefined;
        }
        case FieldTypes.SIGNATURE:
            // only "is it signed" is ever asked (is empty / is not empty): a marker, never the typed name or anything about the image
            return answer.signature && typeof answer.signature === 'object' && Object.keys(answer.signature).length ? 'signed' : undefined;
        case FieldTypes.PHONE_NUMBER:
            return answer.phoneNumber ?? answer.phone_number;
        case FieldTypes.MULTIPLE_CHOICE:
        case FieldTypes.DROP_DOWN:
            // multi-select stores `choices.values`, single stores `choice.value`
            return answer.choices?.values ?? answer.choice?.value;
        case FieldTypes.CALCULATED: {
            // The computed value (utils/calculated-fields.ts) as the plain
            // text the expressions spec prints: 12.5, "true", a text. The
            // server reads its own value the same way.
            const value = answer.number ?? answer.boolean ?? answer.text;
            return value === undefined || value === null || value === '' ? undefined : formatExpressionValue(value);
        }
        default:
            return answer.text ?? answer.value;
    }
}

/** Is `text` longer than `max` code points? Cheap for a long one: more than twice `max` UTF-16 units is over without counting. */
const overCap = (text: string, max: number): boolean => text.length > 2 * max || (text.length > max && codePointLength(text) > max);

/**
 * `value` as a condition may read it when `textMax` is set (field rules): a
 * text longer than that counts as empty (a list drops such texts), as the
 * expressions' inputs do, so the cost of a condition does not grow with the
 * length of an answer. Twin of the server's `_capped`.
 */
function capped(value: any, textMax?: number): any {
    if (textMax === undefined) return value;
    if (typeof value === 'string') return overCap(value, textMax) ? undefined : value;
    if (Array.isArray(value)) return value.filter((item) => !(typeof item === 'string' && overCap(item, textMax)));
    return value;
}

const isEmpty = (v: any): boolean => v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);

const toNumber = (v: any): number => (Array.isArray(v) ? NaN : Number(v));

/** Items of a repeating group answer (`{type: 'group', items: [...]}`), never undefined. */
export function getGroupAnswerItems(answer: any): Array<Record<string, any>> {
    const items = answer?.items;
    return Array.isArray(items) ? items.map((item) => (item && typeof item === 'object' ? item : {})) : [];
}

/**
 * Conditions on a repeating group as a whole (the group is the condition's
 * field): the number of items, or whether ANY / ALL items match a condition on
 * one of the group's questions. Each item is evaluated with its own answers
 * layered over the rest of the form's, exactly like visibility inside an item.
 * ALL over zero items does not match (there is nothing to match).
 */
function evaluateGroupCondition(answers: Record<string, any>, condition: LogicCondition, sources?: LogicSources, textMax?: number): boolean {
    const items = getGroupAnswerItems(answers?.[condition.fieldId]);
    if (condition.groupMode === 'COUNT') {
        return compareValue(items.length, condition.comparison, condition.value, textMax);
    }
    if (!condition.childFieldId) return false;
    const child: LogicCondition = { fieldId: condition.childFieldId, fieldType: condition.childFieldType ?? '', comparison: condition.comparison, value: condition.value };
    const matches = items.map((item) => evaluateCondition({ ...(answers || {}), ...item }, child, sources, textMax));
    if (condition.groupMode === 'ANY') return matches.some(Boolean);
    if (condition.groupMode === 'ALL') return matches.length > 0 && matches.every(Boolean);
    return false;
}

const CHOICE_TYPES = new Set<string>([FieldTypes.MULTIPLE_CHOICE, FieldTypes.DROP_DOWN]);
const NEGATED_COMPARISONS = new Set<string>([Comparison.IS_NOT_EQUAL, Comparison.DOES_NOT_CONTAIN]);

const filledText = (v: any): v is string | number => (typeof v === 'string' && v !== '') || typeof v === 'number';

/** What a chosen option answers to: its id, its text and its label (an unknown id: itself). */
function choiceTokens(chosen: string, source?: StandardFormFieldDto): string[] {
    const choice = source?.properties?.choices?.find((c) => c?.id === chosen);
    if (!choice) return [chosen];
    return [...new Set([chosen, choice.value, choice.label].filter(filledText).map(String))];
}

/**
 * A choice answer as the respondent saw it, for display (answer piping): the
 * text of each chosen option, as the question shows it (its text, else its
 * label, else "Item <n>"), then the text typed as "Other". An id the question
 * no longer has is shown as stored. Undefined when nothing is chosen.
 * Several answers give a list; one answer a string.
 */
export function choiceAnswerText(answer: any, field: StandardFormFieldDto | undefined): string | string[] | undefined {
    // options from a source are not in the form: the text stored with the answer
    if (field?.properties?.optionSource) {
        const texts = sourcedAnswerTexts(answer);
        return answer?.choices ? (texts.length ? texts : undefined) : texts[0];
    }
    const choices = field?.properties?.choices ?? [];
    const text = (chosen: string): string => {
        const index = choices.findIndex((c) => c?.id === chosen);
        if (index < 0) return chosen;
        const choice = choices[index];
        return filledText(choice.value) ? String(choice.value) : filledText(choice.label) ? String(choice.label) : `Item ${index + 1}`;
    };
    const many = answer?.choices;
    if (many && typeof many === 'object' && !Array.isArray(many)) {
        const texts = (Array.isArray(many.values) ? many.values : []).filter(filledText).map((v: string | number) => text(String(v)));
        if (filledText(many.other)) texts.push(String(many.other));
        return texts.length ? texts : undefined;
    }
    const one = answer?.choice;
    if (filledText(one?.value)) return text(String(one.value));
    return filledText(one?.other) ? String(one.other) : undefined;
}

/**
 * A condition on a choice question. The answer holds option ids (and the text
 * typed as "Other"); the condition names an option by its text or its id.
 * Several answers: one list of everything the chosen options answer to, plus
 * the "Other" text, compared as a list (membership). One answer: the
 * condition holds when it holds for any of what the option answers to; "is
 * not" and "does not contain" when they hold for all of them.
 */
function evaluateChoiceCondition(answer: any, source: StandardFormFieldDto | undefined, comparison: Comparison, target: any, textMax?: number): boolean {
    const many = answer?.choices;
    if (many && typeof many === 'object' && !Array.isArray(many)) {
        const tokens = (Array.isArray(many.values) ? many.values : []).filter(filledText).flatMap((v: string | number) => choiceTokens(String(v), source));
        if (filledText(many.other)) tokens.push(String(many.other));
        return compareValue(tokens, comparison, target, textMax);
    }
    const one = answer?.choice;
    const tokens = [...(filledText(one?.value) ? choiceTokens(String(one.value), source) : []), ...(filledText(one?.other) ? [String(one.other)] : [])];
    if (!tokens.length) return compareValue(undefined, comparison, target, textMax);
    const results = tokens.map((token) => compareValue(token, comparison, target, textMax));
    return NEGATED_COMPARISONS.has(comparison) ? results.every(Boolean) : results.some(Boolean);
}

const ORDERED_COMPARISONS = new Set<string>([Comparison.GREATER_THAN, Comparison.GREATER_THAN_EQUAL, Comparison.LESS_THAN, Comparison.LESS_THAN_EQUAL]);
/**
 * Questions whose answer is text that sorts as the thing it is: a time
 * ("HH:MM") or a calendar day ("YYYY-MM-DD"). Ordered comparisons compare
 * them as such (both sides must be well-formed, else the condition does not
 * hold); everything else reads numbers. Twin of the server's
 * `_ordered_text_condition`.
 */
const ORDERED_TEXT_TYPES = new Set<string>([FieldTypes.TIME, FieldTypes.DAY, FieldTypes.DATE]);

function compareOrderedText(value: any, fieldType: string, comparison: Comparison, target: any): boolean {
    const wellFormed = fieldType === FieldTypes.TIME ? isTime : isIsoDate;
    if (!wellFormed(value) || !wellFormed(target)) return false;
    switch (comparison) {
        case Comparison.GREATER_THAN:
            return value > target;
        case Comparison.GREATER_THAN_EQUAL:
            return value >= target;
        case Comparison.LESS_THAN:
            return value < target;
        default:
            return value <= target;
    }
}

/**
 * A condition on a date range: includes a day (CONTAINS), does not include it,
 * starts after it (GREATER_THAN), ends before it (LESS_THAN), is empty or is
 * not. Needs both days and a well-formed target day, else it does not hold.
 * Twin of the server's `_date_range_condition`.
 */
export function compareDateRange(answer: any, comparison: Comparison | string, target: any): boolean {
    const { start, end } = dateRangeDays(answer);
    if (comparison === Comparison.IS_EMPTY) return start === undefined && end === undefined;
    if (comparison === Comparison.IS_NOT_EMPTY) return start !== undefined || end !== undefined;
    if (start === undefined || end === undefined || !isIsoDate(target)) return false;
    switch (comparison) {
        case Comparison.CONTAINS:
            return start <= target && target <= end;
        case Comparison.DOES_NOT_CONTAIN:
            return !(start <= target && target <= end);
        case Comparison.GREATER_THAN:
            return start > target;
        case Comparison.LESS_THAN:
            return end < target;
        default:
            return false;
    }
}

/** What a condition on an address question may ask: about its postcode (backend `ADDRESS_COMPARISONS`). */
export const ADDRESS_COMPARISONS = new Set<string>([Comparison.IS_EQUAL, Comparison.IS_NOT_EQUAL, Comparison.STARTS_WITH, Comparison.IS_EMPTY, Comparison.IS_NOT_EMPTY]);
/** A signature is either there or not: nothing else can be asked of it (backend `SIGNATURE_COMPARISONS`). */
const SIGNATURE_COMPARISONS = new Set<string>([Comparison.IS_EMPTY, Comparison.IS_NOT_EMPTY]);

/** What a condition on a tags question may ask (backend `TAGS_COMPARISONS`). */
const TAGS_COMPARISONS = new Set<string>([Comparison.CONTAINS, Comparison.DOES_NOT_CONTAIN, Comparison.IS_EMPTY, Comparison.IS_NOT_EMPTY]);

function evaluateCondition(answers: Record<string, any>, condition: LogicCondition, sources?: LogicSources, textMax?: number): boolean {
    if (condition.groupMode) return evaluateGroupCondition(answers, condition, sources, textMax);
    const answer = answers?.[condition.fieldId];
    if (condition.fieldType === FieldTypes.DATE_RANGE) return compareDateRange(answer, condition.comparison, condition.value);
    if (condition.fieldType === FieldTypes.ADDRESS) {
        // only the postcode is read (the spec check and the operations refuse the other comparisons)
        if (!ADDRESS_COMPARISONS.has(condition.comparison)) return false;
        return compareValue(getComparableAnswerValue(answer, condition.fieldType), condition.comparison, condition.value, textMax);
    }
    if (condition.fieldType === FieldTypes.SIGNATURE) {
        // only "is it signed" can be asked (the spec check and the operations refuse the other comparisons)
        if (!SIGNATURE_COMPARISONS.has(condition.comparison)) return false;
        return compareValue(getComparableAnswerValue(answer, condition.fieldType), condition.comparison, undefined, textMax);
    }
    if (condition.fieldType === FieldTypes.TAGS) {
        // a list of labels has no beginning, end or order to compare: only these hold (backend TAGS_COMPARISONS)
        if (!TAGS_COMPARISONS.has(condition.comparison)) return false;
        // tags are compared ignoring case
        const tags = getComparableAnswerValue(answer, condition.fieldType) as string[] | undefined;
        return compareValue(tags?.length ? tags.map(tagKey) : tags, condition.comparison, typeof condition.value === 'string' ? tagKey(condition.value) : condition.value, textMax);
    }
    if (ORDERED_TEXT_TYPES.has(condition.fieldType) && ORDERED_COMPARISONS.has(condition.comparison)) return compareOrderedText(getComparableAnswerValue(answer, condition.fieldType), condition.fieldType, condition.comparison, condition.value);
    if (CHOICE_TYPES.has(condition.fieldType)) return evaluateChoiceCondition(answer, sources?.[condition.fieldId], condition.comparison, condition.value, textMax);
    const value = getComparableAnswerValue(answer, condition.fieldType);
    return compareValue(value, condition.comparison, condition.value, textMax);
}

function compareValue(read: any, comparison: Comparison, target: any, textMax?: number): boolean {
    const value = capped(read, textMax);
    switch (comparison) {
        case Comparison.IS_EMPTY:
            return isEmpty(value);
        case Comparison.IS_NOT_EMPTY:
            return !isEmpty(value);
        case Comparison.IS_EQUAL:
            if (Array.isArray(value)) return value.map(String).includes(String(target));
            return String(value ?? '') === String(target ?? '');
        case Comparison.IS_NOT_EQUAL:
            if (Array.isArray(value)) return !value.map(String).includes(String(target));
            return String(value ?? '') !== String(target ?? '');
        case Comparison.CONTAINS:
            if (Array.isArray(value)) return value.map(String).includes(String(target));
            return String(value ?? '').toLowerCase().includes(String(target ?? '').toLowerCase());
        case Comparison.DOES_NOT_CONTAIN:
            if (Array.isArray(value)) return !value.map(String).includes(String(target));
            return !String(value ?? '').toLowerCase().includes(String(target ?? '').toLowerCase());
        case Comparison.GREATER_THAN:
            return toNumber(value) > toNumber(target);
        case Comparison.GREATER_THAN_EQUAL:
            return toNumber(value) >= toNumber(target);
        case Comparison.LESS_THAN:
            return toNumber(value) < toNumber(target);
        case Comparison.LESS_THAN_EQUAL:
            return toNumber(value) <= toNumber(target);
        case Comparison.STARTS_WITH:
            return String(value ?? '').toLowerCase().startsWith(String(target ?? '').toLowerCase());
        case Comparison.ENDS_WITH:
            return String(value ?? '').toLowerCase().endsWith(String(target ?? '').toLowerCase());
        default:
            return false;
    }
}

/**
 * Fold a set of conditions with AND/OR. Empty/incomplete condition sets never match.
 * `sources`: the form's questions by id (`logicSourcesById`), which a condition on a
 * choice question needs to match an option's text; without it, only option ids match.
 * `textMax`: see `capped` (field rules set it; logic does not).
 */
export function evaluateConditions(operator: LogicalOperator | undefined, conditions: LogicCondition[] | undefined, answers: Record<string, any>, sources?: LogicSources, textMax?: number): boolean {
    const valid = (conditions ?? []).filter((c) => c && c.fieldId && c.comparison);
    if (valid.length === 0) return false;
    if (operator === LogicalOperator.OR) return valid.some((c) => evaluateCondition(answers, c, sources, textMax));
    return valid.every((c) => evaluateCondition(answers, c, sources, textMax));
}

/** Are a rule's conditions satisfied given the current answers? */
export function areConditionsMet(logic: FieldConditionalLogic, answers: Record<string, any>, sources?: LogicSources): boolean {
    return evaluateConditions(logic?.operator, logic?.conditions, answers, sources);
}

/**
 * Resolve where a slide should send the responder on "Next", given the answers.
 * Returns the first matching jump rule's target (a slide id or JUMP_TARGET_SUBMIT),
 * or null when no rule matches (caller falls back to the linear next page).
 */
export function resolveJumpTargetId(slide: StandardFormFieldDto | undefined, answers: Record<string, any>, sources?: LogicSources): string | null {
    return resolveJump(slide, answers, sources)?.target || null;
}

/**
 * The jump that decides where the responder goes on "Next": the first whose
 * conditions match, or null. With a JUMP_TARGET_SUBMIT target its `endingId`
 * names the ending to show (utils/endings.ts).
 */
export function resolveJump(slide: StandardFormFieldDto | undefined, answers: Record<string, any>, sources?: LogicSources): PageJump | null {
    const jumps = slide?.properties?.jumps as PageJump[] | undefined;
    if (!Array.isArray(jumps)) return null;
    for (const jump of jumps) {
        if (jump?.conditions?.length && evaluateConditions(jump.operator, jump.conditions, answers, sources)) return jump;
    }
    return null;
}

/**
 * Is this field hidden right now given the answers so far?
 * Fields without a logic rule (or with no usable conditions) are always visible.
 */
export function isFieldHiddenByLogic(field: StandardFormFieldDto, answers: Record<string, any>, sources?: LogicSources): boolean {
    const hides = (logic: FieldConditionalLogic): boolean => {
        const met = areConditionsMet(logic, answers, sources);
        return logic.action === 'SHOW' ? !met : met;
    };
    const logic = field?.properties?.logic as FieldConditionalLogic | undefined;
    if (logic && logic.action && logic.conditions?.length > 0 && hides(logic)) return true;
    // a layout container that hides it (utils/layout-containers.ts)
    const outer = field?.id ? (sources as any)?.[CONTAINER_LOGIC]?.[field.id] : undefined;
    return !!outer && outer.some(hides);
}

/** Ids of every field in a slide that should be hidden right now. */
export function getHiddenFieldIds(fields: Array<StandardFormFieldDto> | undefined, answers: Record<string, any>, sources?: LogicSources): Set<string> {
    const hidden = new Set<string>();
    fields?.forEach((field) => {
        if (isFieldHiddenByLogic(field, answers, sources)) hidden.add(field.id);
    });
    return hidden;
}

/** Is a jump's target still a real destination (an existing slide, or "submit")? */
export function isJumpTargetValid(target: string | undefined, slideIds: Set<string>): boolean {
    return !!target && (target === JUMP_TARGET_SUBMIT || slideIds.has(target));
}

/** Does a field carry a usable conditional-visibility rule? */
export function fieldHasLogic(field: StandardFormFieldDto | undefined): boolean {
    return !!(field?.properties?.logic as FieldConditionalLogic | undefined)?.conditions?.length;
}

/** Does a slide carry any logic — page jumps, or a field with a visibility rule? */
export function slideHasLogic(slide: StandardFormFieldDto | undefined): boolean {
    const jumps = slide?.properties?.jumps as PageJump[] | undefined;
    if (jumps?.some((j) => j?.conditions?.length)) return true;
    return !!slide?.properties?.fields?.some((f) => fieldHasLogic(f));
}

/** Aggregated flow traffic replayed from submitted responses. */
export interface FlowTraffic {
    /** submissions counted */
    total: number;
    /** slide id → number of submissions whose path visited it */
    nodeVisits: Map<string, number>;
    /** `${slideId}:${jumpIndex}` → traversals of that jump edge */
    jumpTraversals: Map<string, number>;
    /** slide id (or '__welcome__') → times the linear "next" edge out of it was taken */
    nextTraversals: Map<string, number>;
    /** ending id a jump chose → submissions that ended there; '' = no ending chosen (the default) */
    endingVisits: Map<string, number>;
}

/**
 * One submission's path through the flow: the pages it went through, the jump
 * taken out of each (its index; absent = the linear "next"), and whether and
 * where it ended. The server sends these for the flow view's insights
 * (`GET .../submission-paths`, the twin of `submission_path` in the backend's
 * answer_validation.py), so the builder never needs the answers themselves.
 */
export interface SubmissionPath {
    steps: Array<{ pageId: string; jump?: number | null }>;
    /** reached the end of the form: a jump to "submit" or past the last page */
    ended: boolean;
    /** the ending a jump to "submit" named; absent = the default ending */
    endingId?: string | null;
}

/**
 * Replay the forward path a submission implies through the flow, using the SAME
 * jump resolution the responder ran. Approximation is explicit: we replay from
 * final answers, so mid-fill back-tracking isn't represented, and paths are
 * capped at 2×pages+2 hops in case persisted rules loop.
 */
export function submissionPath(slides: Array<StandardFormFieldDto>, answers: Record<string, any>, sources: LogicSources = logicSourcesById(slides)): SubmissionPath {
    const slideIds = new Set(slides.map((s) => s.id));
    const indexById = new Map(slides.map((s, i) => [s.id, i]));
    const steps: SubmissionPath['steps'] = [];
    const maxHops = slides.length * 2 + 2;
    let index = 0;
    for (let hop = 0; hop < maxHops && index >= 0 && index < slides.length; hop++) {
        const slide = slides[index];
        // Find the first matching jump (same order semantics as the responder).
        const jumps = (slide.properties?.jumps as PageJump[] | undefined) ?? [];
        const matchIdx = jumps.findIndex((j) => j?.conditions?.length && isJumpTargetValid(j.target, slideIds) && evaluateConditions(j.operator, j.conditions, answers ?? {}, sources));
        if (matchIdx < 0) {
            steps.push({ pageId: slide.id });
            index += 1;
            continue;
        }
        steps.push({ pageId: slide.id, jump: matchIdx });
        const target = jumps[matchIdx].target;
        if (target === JUMP_TARGET_SUBMIT) return { steps, ended: true, endingId: jumps[matchIdx].endingId };
        index = indexById.get(target) ?? -1;
    }
    // Past the last page: a plain submit, the default ending.
    return { steps, ended: index >= slides.length };
}

/** Count submission paths onto the flow's nodes and edges. */
export function flowTrafficFromPaths(paths: Array<SubmissionPath>): FlowTraffic {
    const traffic: FlowTraffic = { total: paths.length, nodeVisits: new Map(), jumpTraversals: new Map(), nextTraversals: new Map(), endingVisits: new Map() };
    const bump = (map: Map<string, number>, key: string) => map.set(key, (map.get(key) ?? 0) + 1);
    paths.forEach((path) => {
        bump(traffic.nextTraversals, '__welcome__'); // welcome → first page
        (path?.steps ?? []).forEach(({ pageId, jump }) => {
            bump(traffic.nodeVisits, pageId);
            if (typeof jump === 'number') bump(traffic.jumpTraversals, `${pageId}:${jump}`);
            else bump(traffic.nextTraversals, pageId);
        });
        if (path?.ended) bump(traffic.endingVisits, path.endingId ?? '');
    });
    return traffic;
}

/** Replay answers here and count them (the flow view gets the paths from the server instead). */
export function computeFlowTraffic(slides: Array<StandardFormFieldDto>, answersList: Array<Record<string, any>>): FlowTraffic {
    const sources = logicSourcesById(slides);
    return flowTrafficFromPaths(answersList.map((answers) => submissionPath(slides, answers, sources)));
}

/**
 * Drop logic that can no longer evaluate after a page/question is deleted:
 * conditions pointing at fields that no longer exist — or that became internal
 * (respondents never answer those) — and any rule left with zero conditions. Mutates the slide array in place and returns it. Jump targets that
 * point at a deleted page are left intact so the UI can surface them as broken
 * rather than silently rewriting where a rule sends people.
 */
export function pruneOrphanedConditions(slides: Array<StandardFormFieldDto>): Array<StandardFormFieldDto> {
    const fieldIds = new Set<string>();
    slides.forEach((slide) =>
        slide?.properties?.fields?.forEach((f) => {
            // Internal fields are never answered by respondents: not a logic source.
            if (!f.internal) fieldIds.add(f.id);
            // Questions of a repeating group can be referenced by their siblings.
            if (f?.type === FieldTypes.GROUP && f.properties?.repeat) f.properties.fields?.forEach((child) => !child.internal && fieldIds.add(child.id));
        })
    );
    const isLive = (c: LogicCondition) => !!c && fieldIds.has(c.fieldId) && (!c.groupMode || c.groupMode === 'COUNT' || (!!c.childFieldId && fieldIds.has(c.childFieldId)));
    const liveConditions = (conditions: LogicCondition[] | undefined) => (conditions ?? []).filter(isLive);
    const pruneFieldLogic = (field: StandardFormFieldDto) => {
        const logic = field?.properties?.logic;
        if (!logic) return;
        const conditions = liveConditions(logic.conditions);
        if (conditions.length) field.properties!.logic = { ...logic, conditions };
        else delete field.properties!.logic;
    };

    slides.forEach((slide) => {
        const props = slide?.properties;
        if (!props) return;

        if (Array.isArray(props.jumps)) {
            const jumps = props.jumps.map((j) => ({ ...j, conditions: liveConditions(j.conditions) })).filter((j) => j.conditions.length > 0);
            if (jumps.length) props.jumps = jumps;
            else delete props.jumps;
        }

        props.fields?.forEach((field) => {
            pruneFieldLogic(field);
            if (field?.type === FieldTypes.GROUP && field.properties?.repeat) field.properties.fields?.forEach(pruneFieldLogic);
        });
    });

    // Choice logic reads questions too (conditions of an option, an option filter): repaired with them.
    pruneOptionLogic(slides);

    return slides;
}
